import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Trash2, Edit, FileText, Eye, RefreshCw } from 'lucide-react';
import { Button, Card } from 'react-bootstrap';
import Swal from 'sweetalert2';
import axios from 'axios';

const WorkWeDo = () => {
  const navigate = useNavigate();
  
  const [cards, setCards] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewDetailsModalOpen, setIsViewDetailsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({ 
    title: '',
    slug: '',
    order: '',
    isActive: true,
    url: '',
    image: null
  });
  
  const fileInputRef = useRef(null);

  // API endpoints
  const API_BASE_URL = 'https://backend.dlapakistan.com/api/about';
  const SERVICES_API_URL = `${API_BASE_URL}/services/`;
  const SERVICE_BY_ID_URL = (id) => `${API_BASE_URL}/services/id/${id}/`;

  // Function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/400x250/f3f4f6/cccccc?text=No+Image';
    
    if (typeof imagePath === 'string') {
      if (imagePath.startsWith('http') || imagePath.startsWith('https') || imagePath.startsWith('data:')) {
        return imagePath;
      }
      
      if (imagePath.startsWith('/media/')) {
        return `https://backend.dlapakistan.com${imagePath}`;
      }
      
      if (imagePath.includes('/')) {
        return `https://backend.dlapakistan.com${imagePath}`;
      }
      
      if (imagePath) {
        return `https://backend.dlapakistan.com/media/services/${imagePath}`;
      }
    }
    
    return 'https://via.placeholder.com/400x250/f3f4f6/cccccc?text=No+Image';
  };

  // Fetch data from API on component mount
  useEffect(() => {
    fetchCardsFromAPI();
  }, []);

  const fetchCardsFromAPI = async () => {
    try {
      setApiLoading(true);
      setError(null);
      const response = await axios.get(SERVICES_API_URL);
      
      if (response.data && Array.isArray(response.data)) {
        const apiCards = response.data.map(service => ({
          id: service.id,
          title: service.title,
          slug: service.slug,
          order: service.order,
          isActive: service.is_active,
          image: getImageUrl(service.image),
          button_text: service.button_text || 'Find out more',
          url: service.url || '',
          absolute_url: service.absolute_url || '',
          description: '',
          route: '',
          details: {
            subtitle: '',
            content: ''
          },
          createdAt: service.created_at || new Date().toISOString()
        }));
        
        setCards(apiCards);
      }
      setApiLoading(false);
    } catch (err) {
      console.error("Error fetching from API:", err);
      setError("Failed to load data from server. Using local storage.");
      setApiLoading(false);
    }
  };

  // Function to create/update card via API
  const saveCardToAPI = async (cardData, isEditMode, cardId = null) => {
    try {
      setLoading(true);
      
      const formDataObj = new FormData();
      formDataObj.append('title', cardData.title);
      formDataObj.append('slug', cardData.slug);
      formDataObj.append('order', cardData.order);
      formDataObj.append('is_active', cardData.isActive);
      formDataObj.append('button_text', cardData.button_text || 'Find out more');
      
      if (cardData.url) {
        formDataObj.append('url', cardData.url);
      }
      
      if (cardData.image && typeof cardData.image !== 'string') {
        formDataObj.append('image', cardData.image);
      }
      
      let response;
      
      if (isEditMode && cardId) {
        response = await axios.put(SERVICE_BY_ID_URL(cardId), formDataObj, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
        Swal.fire('Success', 'Card updated successfully!', 'success');
      } else {
        response = await axios.post(SERVICES_API_URL, formDataObj, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
        Swal.fire('Success', 'Card added successfully!', 'success');
      }
      
      await fetchCardsFromAPI();
      return true;
      
    } catch (error) {
      console.error('Error saving card to API:', error);
      Swal.fire('Error', error.response?.data?.detail || 'Failed to save card', 'error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Function to delete card via API
  const deleteCardFromAPI = async (cardId) => {
    try {
      await axios.delete(SERVICE_BY_ID_URL(cardId));
      Swal.fire('Deleted!', 'Card has been deleted.', 'success');
      return true;
    } catch (error) {
      console.error('Error deleting card:', error);
      Swal.fire('Error', 'Failed to delete card', 'error');
      return false;
    }
  };

  // Auto-generate slug when title changes
  useEffect(() => {
    if (formData.title && !isEditMode) {
      const generatedSlug = formData.title
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, slug: generatedSlug }));
    }
  }, [formData.title, isEditMode]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
        setFormData(prev => ({ ...prev, image: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cardData = {
      title: formData.title,
      slug: formData.slug || formData.title.toLowerCase().replace(/\s+/g, '-'),
      order: formData.order ? parseInt(formData.order) : cards.length + 1,
      isActive: formData.isActive,
      url: formData.url || '',
      image: formData.image,
      button_text: 'Find out more'
    };

    const apiSuccess = await saveCardToAPI(
      cardData, 
      isEditMode, 
      isEditMode ? cards[currentIndex]?.id : null
    );
    
    if (!apiSuccess) {
      const newCard = {
        id: isEditMode ? cards[currentIndex].id : Date.now(),
        title: cardData.title,
        slug: cardData.slug,
        order: cardData.order,
        isActive: cardData.isActive,
        image: imagePreview || 'https://via.placeholder.com/400x250/f3f4f6/cccccc?text=No+Image',
        button_text: 'Find out more',
        url: cardData.url,
        absolute_url: cardData.url ? cardData.url : `/about/work-we-do/find-out-more/${cardData.slug}`,
        description: '',
        route: '',
        details: { subtitle: '', content: '' },
        createdAt: new Date().toISOString()
      };

      if (isEditMode) {
        const updatedCards = [...cards];
        updatedCards[currentIndex] = newCard;
        updatedCards.sort((a, b) => a.order - b.order);
        setCards(updatedCards);
        localStorage.setItem('workWeDoCards', JSON.stringify(updatedCards));
      } else {
        const newCards = [...cards, newCard];
        newCards.sort((a, b) => a.order - b.order);
        setCards(newCards);
        localStorage.setItem('workWeDoCards', JSON.stringify(newCards));
      }
    }
    
    handleCloseModal();
  };

  const handleDelete = async (index) => {
    const confirmed = await Swal.fire({
      title: 'Are you sure?',
      text: 'This cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (confirmed.isConfirmed) {
      const cardId = cards[index]?.id;
      
      if (cardId && typeof cardId === 'number') {
        const apiSuccess = await deleteCardFromAPI(cardId);
        if (apiSuccess) {
          await fetchCardsFromAPI();
          return;
        }
      }
      
      const newCards = cards.filter((_, i) => i !== index);
      setCards(newCards);
      localStorage.setItem('workWeDoCards', JSON.stringify(newCards));
      
      Swal.fire('Deleted!', 'Card has been deleted.', 'success');
    }
  };

  const handleEdit = (index) => {
    const card = cards[index];
    setCurrentIndex(index);
    setFormData({ 
      title: card.title, 
      slug: card.slug || '',
      order: card.order || '',
      isActive: card.isActive !== false,
      url: card.url || '',
      image: null 
    });
    setImagePreview(card.image);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleViewDetails = (index) => {
    setCurrentIndex(index);
    setIsViewDetailsModalOpen(true);
  };

  // Updated: Navigate to Find Out More page with slug
  const handleFindOutMore = (card) => {
    navigate(`/About-Dla/WorkWeDo/Findoutmore/${card.slug}`, { 
      state: { 
        cardData: card 
      } 
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentIndex(null);
    setFormData({ 
      title: '', 
      slug: '',
      order: '',
      isActive: true,
      url: '',
      image: null 
    });
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCloseViewDetailsModal = () => {
    setIsViewDetailsModalOpen(false);
    setCurrentIndex(null);
  };

  const triggerFileInput = () => fileInputRef.current?.click();
  const removeImage = () => { 
    setImagePreview(null); 
    setFormData(prev => ({ ...prev, image: null })); 
    if (fileInputRef.current) fileInputRef.current.value = ''; 
  };

  const toggleCardStatus = async (index) => {
    const card = cards[index];
    const newStatus = !card.isActive;
    const cardId = card.id;
    
    try {
      if (cardId && typeof cardId === 'number') {
        await axios.patch(SERVICE_BY_ID_URL(cardId), {
          is_active: newStatus
        });
      }
      
      const updatedCards = [...cards];
      updatedCards[index] = {
        ...updatedCards[index],
        isActive: newStatus
      };
      
      setCards(updatedCards);
      localStorage.setItem('workWeDoCards', JSON.stringify(updatedCards));
      
      await Swal.fire({
        title: 'Status Updated',
        text: `Card is now ${newStatus ? 'Active' : 'Inactive'}`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
      
      await fetchCardsFromAPI();
      
    } catch (error) {
      console.error('Error updating status:', error);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  // Load from localStorage on initial load if API fails
  useEffect(() => {
    if (!apiLoading && cards.length === 0 && error) {
      const savedCards = localStorage.getItem('workWeDoCards');
      if (savedCards) {
        setCards(JSON.parse(savedCards));
        setError(null);
      }
    }
  }, [apiLoading, cards.length, error]);

  const sortedCards = [...cards].sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-10 px-4 sm:px-6 lg:px-8 max-w-8xl mx-auto">
        {/* Header section */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-12">
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-green-700 mb-4">Work We Do</h1>
            <hr className="border-t-2 border-green-600 mb-6 w-24" />
            <p className="text-gray-700 text-lg leading-relaxed">
              Manage your cards with Title, Slug, Order and Active status.
              {apiLoading && <span className="ml-2 text-green-600 animate-pulse">Loading from API...</span>}
              {error && <span className="ml-2 text-yellow-600">Using local storage</span>}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
            <Button 
              onClick={() => {
                setIsModalOpen(true);
                setIsEditMode(false);
                setFormData({ 
                  title: '', 
                  slug: '',
                  order: '',
                  isActive: true,
                  url: '',
                  image: null 
                });
                setImagePreview(null);
              }} 
              className="flex items-center px-8 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold hover:from-green-700 hover:to-emerald-700 shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" /> Add Card
            </Button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">API Connection Issue</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{error}</p>
                  <p className="mt-1">Changes will be saved locally.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading state */}
        {apiLoading && (
          <div className="text-center py-20">
            <div className="inline-flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600 mb-4"></div>
              <p className="text-gray-600 text-lg">Loading cards from API...</p>
            </div>
          </div>
        )}

        {/* Cards Grid */}
        {!apiLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 justify-items-center">
            {sortedCards.length > 0 ? (
              sortedCards.map((card, index) => (
                <Card 
                  key={card.id} 
                  className={`bg-white rounded-xl shadow-lg overflow-hidden max-w-xs w-full relative group ${
                    !card.isActive ? 'opacity-60' : ''
                  }`}
                >
                  {!card.isActive && (
                    <div className="absolute top-2 left-2 z-10">
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">Inactive</span>
                    </div>
                  )}
                  
                  <div className="h-56 overflow-hidden bg-gray-100">
                    <img 
                      src={getImageUrl(card.image)}
                      alt={card.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        console.error('Image failed to load:', card.image);
                        e.target.src = 'https://via.placeholder.com/400x250/f3f4f6/cccccc?text=No+Image';
                      }}
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h3>
                    
                    <div className="space-y-1 mb-3">
                      <p className="text-gray-600 text-sm">
                        <span className="font-medium">Slug:</span> {card.slug}
                      </p>
                      <p className="text-gray-600 text-sm">
                        <span className="font-medium">Order:</span> {card.order || 'N/A'}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleFindOutMore(card)}
                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200"
                      >
                        {card.button_text || 'Find Out More'}
                      </Button>
                      <Button 
                        onClick={() => handleViewDetails(index)}
                        className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                        title="View card details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Button 
                        onClick={() => handleEdit(index)} 
                        className="p-2.5 rounded-lg hover:bg-green-50 transition-all duration-200 shadow"
                        title="Edit card"
                      >
                        <Edit className="w-4 h-4 text-green-600" />
                      </Button>
                      <Button 
                        onClick={() => handleDelete(index)} 
                        className="p-2.5 rounded-lg hover:bg-red-50 transition-all duration-200 shadow"
                        title="Delete card"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <div className="bg-white rounded-xl p-8 shadow-lg max-w-md mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                    <Plus className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Cards Found</h3>
                  <p className="text-gray-600 mb-6">Start by adding your first card.</p>
                  <Button 
                    onClick={() => {
                      setIsModalOpen(true);
                      setIsEditMode(false);
                      setFormData({ 
                        title: '', 
                        slug: '',
                        order: '',
                        isActive: true,
                        url: '',
                        image: null 
                      });
                      setImagePreview(null);
                    }} 
                    className="flex items-center justify-center mx-auto px-6 py-3 rounded-lg bg-green-600 text-white hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Your First Card
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Card Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-10 px-8 py-5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold">{isEditMode ? 'Edit Card' : 'Add Card'}</h2>
              <Button onClick={handleCloseModal} className="p-2 hover:bg-white/20 rounded-full transition-colors duration-200">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-8 space-y-6">
                {/* Title Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Title (Required) *
                  </label>
                  <input 
                    type="text" 
                    value={formData.title} 
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, title: e.target.value }));
                      if (!isEditMode) {
                        const generatedSlug = e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^a-z0-9-]/g, '');
                        setFormData(prev => ({ ...prev, slug: generatedSlug }));
                      }
                    }} 
                    required 
                    disabled={loading}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-500 disabled:bg-gray-100" 
                    placeholder="Enter card title"
                  />
                </div>

                {/* Slug Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Slug (Required) *
                  </label>
                  <input 
                    type="text" 
                    value={formData.slug} 
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} 
                    required 
                    disabled={loading}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-500 disabled:bg-gray-100" 
                    placeholder="card-title-slug"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Automatically generated from title. This will be used in the URL.
                  </p>
                </div>

                {/* Order Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Order (Required) *
                  </label>
                  <input 
                    type="number" 
                    min="1"
                    value={formData.order} 
                    onChange={(e) => setFormData(prev => ({ ...prev, order: e.target.value }))} 
                    required 
                    disabled={loading}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-500 disabled:bg-gray-100" 
                    placeholder="1"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Determine display order (smaller number appears first)
                  </p>
                </div>

                {/* URL Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    External URL (Optional)
                  </label>
                  <input 
                    type="url" 
                    value={formData.url} 
                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))} 
                    disabled={loading}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-500 disabled:bg-gray-100" 
                    placeholder="https://example.com"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    External link (optional). If empty, it will go to Find Out More page.
                  </p>
                </div>

                {/* Is Active Checkbox */}
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    disabled={loading}
                    className="w-5 h-5 text-green-600 rounded focus:ring-green-500 disabled:opacity-50"
                  />
                  <label htmlFor="isActive" className="ml-3 text-sm font-semibold text-gray-700">
                    Is Active
                  </label>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Inactive cards will not be visible on frontend
                </p>

                {/* Image Field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Image (Optional)
                  </label>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" disabled={loading} />
                  {imagePreview ? (
                    <div className="relative w-48 h-48 mb-4">
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-xl" />
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                        <Button onClick={triggerFileInput} disabled={loading} className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">Replace</Button>
                        <Button onClick={removeImage} disabled={loading} className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50">Remove</Button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={triggerFileInput} 
                      className={`border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-all ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Plus className="w-8 h-8 mx-auto mb-2 text-green-600" />
                      <p className="text-gray-600 text-sm">Click to upload image</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="px-8 py-6 border-t flex justify-end gap-4 bg-gray-50 rounded-b-2xl">
                <Button 
                  type="button" 
                  onClick={handleCloseModal} 
                  disabled={loading}
                  className="px-6 py-3 border rounded-xl text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={!formData.title.trim() || !formData.slug.trim() || !formData.order || loading} 
                  className="px-8 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  {isEditMode ? 'Update Card' : 'Add Card'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW Details Modal */}
      {isViewDetailsModalOpen && currentIndex !== null && sortedCards[currentIndex] && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-10 px-8 py-5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-2xl flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-white p-1">
                  <img 
                    src={getImageUrl(sortedCards[currentIndex]?.image)}
                    alt={sortedCards[currentIndex]?.title || 'Card'} 
                    className="w-full h-full object-cover rounded-md"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{sortedCards[currentIndex]?.title || 'Card'}</h2>
                  <p className="text-sm text-green-100 opacity-90">
                    ID: {sortedCards[currentIndex]?.id} • Slug: {sortedCards[currentIndex]?.slug}
                  </p>
                </div>
              </div>
              <Button onClick={handleCloseViewDetailsModal} className="p-2 hover:bg-white/20 rounded-full transition-colors duration-200">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                
                {/* Left Column */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Card Image */}
                  <div className="w-full h-64 rounded-xl overflow-hidden bg-gray-100">
                    <img 
                      src={getImageUrl(sortedCards[currentIndex]?.image)}
                      alt={sortedCards[currentIndex]?.title || 'Card'} 
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* URL Info */}
                  {sortedCards[currentIndex]?.url && (
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <h4 className="text-lg font-bold text-blue-800 mb-2">External URL</h4>
                      <a 
                        href={sortedCards[currentIndex].url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 break-all"
                      >
                        {sortedCards[currentIndex].url}
                      </a>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="lg:col-span-1 space-y-6">
                  
                  {/* Card Information Card */}
                  <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                    <h4 className="text-lg font-bold text-green-800 mb-4 flex items-center">
                      <FileText className="w-5 h-5 mr-2" />
                      Card Information
                    </h4>
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <p className="text-sm text-green-600 font-medium">Title</p>
                        <p className="text-gray-800 font-semibold text-lg break-words">
                          {sortedCards[currentIndex]?.title || 'N/A'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-green-600 font-medium">Button Text</p>
                        <p className="text-gray-800 text-sm font-medium">
                          {sortedCards[currentIndex]?.button_text || 'Find out more'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-green-600 font-medium">Slug</p>
                        <p className="text-gray-800 text-sm font-medium break-words">
                          {sortedCards[currentIndex]?.slug || 'N/A'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-green-600 font-medium">Order</p>
                        <p className="text-gray-800 text-sm font-medium">
                          {sortedCards[currentIndex]?.order || 'N/A'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-green-600 font-medium">Status</p>
                        <p className={`text-sm font-medium ${sortedCards[currentIndex]?.isActive ? 'text-green-700' : 'text-red-700'}`}>
                          {sortedCards[currentIndex]?.isActive ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-green-600 font-medium">Created Date</p>
                        <p className="text-gray-800 text-sm">
                          {sortedCards[currentIndex]?.createdAt ? new Date(sortedCards[currentIndex]?.createdAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions Card */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                      <Edit className="w-5 h-5 mr-2 text-green-600" />
                      Quick Actions
                    </h4>
                    <div className="space-y-3">
                      <Button 
                        onClick={() => {
                          handleCloseViewDetailsModal();
                          setTimeout(() => handleEdit(currentIndex), 300);
                        }}
                        className="w-full flex items-center justify-center gap-2 bg-white border border-green-600 text-green-600 px-4 py-3 rounded-lg hover:bg-green-50 transition-all duration-200"
                      >
                        <Edit className="w-4 h-4" />
                        Edit Card
                      </Button>
                      <Button 
                        onClick={() => toggleCardStatus(currentIndex)}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all duration-200 ${
                          sortedCards[currentIndex]?.isActive 
                            ? 'bg-red-50 border border-red-200 text-red-600 hover:bg-red-100' 
                            : 'bg-green-50 border border-green-200 text-green-600 hover:bg-green-100'
                        }`}
                      >
                        {sortedCards[currentIndex]?.isActive ? 'Deactivate Card' : 'Activate Card'}
                      </Button>
                      <Button 
                        onClick={() => handleDelete(currentIndex)}
                        className="w-full flex items-center justify-center gap-2 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg hover:bg-red-100 transition-all duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Card
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-8 py-6 border-t bg-gray-50 rounded-b-2xl flex justify-end">
              <Button 
                type="button" 
                onClick={handleCloseViewDetailsModal} 
                className="px-8 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors duration-200 flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Close View
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkWeDo;